/**
 * DeloadToggleButton — operational quick-action for trainers.
 *
 * Surfaces from anywhere a workout is referenced: BookingPlanner card,
 * WorkoutViewModal header, ClientDetailPage sessions list. Avoids
 * forcing the trainer into the FullScreenWorkoutEditor for what should
 * feel like tagging a week.
 *
 * Optimistic UI: flips local state immediately, rolls back on error.
 *
 * Trainer/admin UI → English copy: "Mark as Deload" / "🪶 Deload".
 */
import React, { useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Variant: 'pill' (compact, fits in lists), 'inline' (text-link in dropdowns/menus).
 *
 * Props:
 *   workoutId   string  — required
 *   isDeload    bool    — current flag (controlled by parent)
 *   onChange    fn(next, workoutId) — called after server confirms; parent can refetch
 *   disabled    bool
 *   variant     'pill' | 'inline'
 *   testIdSuffix optional unique suffix for data-testid (helps disambiguate in lists)
 */
export const DeloadToggleButton = ({
  workoutId,
  isDeload = false,
  onChange,
  disabled = false,
  variant = 'pill',
  testIdSuffix,
}) => {
  const [busy, setBusy] = useState(false);
  // Local optimistic state — diverges from prop briefly during request.
  const [optimistic, setOptimistic] = useState(isDeload);
  // Reset optimistic when prop changes (e.g. parent refetched).
  React.useEffect(() => { setOptimistic(isDeload); }, [isDeload]);

  const toggle = async (e) => {
    e?.stopPropagation?.();
    if (busy || disabled || !workoutId) return;
    const next = !optimistic;
    setOptimistic(next);
    setBusy(true);
    try {
      await axios.patch(`${API}/workouts/${workoutId}/deload`, { is_deload: next });
      if (onChange) onChange(next, workoutId);
    } catch (err) {
      // Rollback
      setOptimistic(!next);
      // eslint-disable-next-line no-console
      console.error('[DeloadToggleButton] failed', err);
    } finally {
      setBusy(false);
    }
  };

  const tid = `deload-toggle-${testIdSuffix || workoutId}`;

  if (variant === 'inline') {
    return (
      <button
        onClick={toggle}
        disabled={busy || disabled}
        className={`text-xs ${optimistic ? 'text-amber-300 hover:text-amber-200' : 'text-slate-400 hover:text-slate-200'} disabled:opacity-50`}
        data-testid={tid}
      >
        {busy ? '…' : (optimistic ? 'Remove Deload' : 'Mark as Deload')}
      </button>
    );
  }

  // pill variant
  return (
    <button
      onClick={toggle}
      disabled={busy || disabled}
      title={optimistic ? 'Marked as deload week' : 'Mark this session as part of a deload week'}
      className={`text-xs px-2 py-0.5 rounded-md border transition-colors disabled:opacity-50 ${
        optimistic
          ? 'bg-amber-500/15 border-amber-400/50 text-amber-200 hover:bg-amber-500/25'
          : 'border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-500'
      }`}
      data-testid={tid}
    >
      {busy ? '…' : (optimistic ? '🪶 Deload' : '+ Deload')}
    </button>
  );
};

export default DeloadToggleButton;
